import type { BrandSurface, Surface, WarmUpPhase } from "../../../shared/types";

const DEFAULT_CAMPAIGN_SURFACE_OPTIONS: Array<{ value: Surface; label: string; platform: string; format: string }> = [
  {
    value: "linkedin",
    label: "LinkedIn",
    platform: "linkedin",
    format: "linkedin-post",
  },
  {
    value: "blog",
    label: "Blog",
    platform: "website",
    format: "article",
  },
  {
    value: "youtube",
    label: "YouTube",
    platform: "youtube",
    format: "youtube-video",
  },
];

const LEGACY_CAMPAIGN_SURFACE_OPTIONS: Array<{ value: Surface; label: string; platform: string; format: string }> = [
  {
    value: "linkedin_personal",
    label: "LinkedIn Personal",
    platform: "linkedin",
    format: "linkedin-post",
  },
  {
    value: "linkedin_derivative",
    label: "LinkedIn Derivative",
    platform: "linkedin",
    format: "linkedin-post",
  },
  {
    value: "youtube_personal",
    label: "YouTube Personal",
    platform: "youtube",
    format: "youtube-video",
  },
];

type CampaignSurfaceOption = { value: Surface; label: string; platform: string | null; format: string | null };

const DESTINATION_SURFACE_KINDS = new Set([
  "website",
  "homepage",
  "landing",
  "landing_page",
  "pricing",
  "shopify",
  "store",
  "hosted_listing",
]);

function titleCasePart(part: string): string {
  return part ? `${part[0]!.toUpperCase()}${part.slice(1)}` : part;
}

function humanizeSurface(surface: string): string {
  return surface
    .split(/[_-]+/g)
    .filter(Boolean)
    .map(titleCasePart)
    .join(" ");
}

function lookupLegacySurfaceOption(surface: string): CampaignSurfaceOption | null {
  const match = [...DEFAULT_CAMPAIGN_SURFACE_OPTIONS, ...LEGACY_CAMPAIGN_SURFACE_OPTIONS]
    .find((option) => option.value === surface);
  return match
    ? {
        value: match.value,
        label: match.label,
        platform: match.platform,
        format: match.format,
      }
    : null;
}

function inferPlatform(surface: string): string | null {
  const normalized = surface.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.startsWith("linkedin")) return "linkedin";
  if (normalized.startsWith("youtube")) return "youtube";
  if (normalized === "x" || normalized === "twitter") return "twitter";
  if (normalized === "newsletter" || normalized === "email") return "email";
  if (normalized === "blog" || normalized === "docs" || normalized === "documentation") return "website";
  if (["reddit", "hackernews", "quora", "producthunt", "indiehackers", "instagram", "tiktok", "threads", "bluesky"].includes(normalized)) {
    return normalized;
  }
  return null;
}

function inferFormat(surface: string): string | null {
  const normalized = surface.trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.startsWith("linkedin")) return "linkedin-post";
  if (normalized.startsWith("youtube")) return "youtube-video";
  if (normalized === "blog" || normalized === "docs" || normalized === "documentation") return "article";
  if (normalized === "newsletter" || normalized === "email") return "newsletter";
  if (["x", "twitter", "reddit", "hackernews", "quora", "producthunt", "indiehackers", "instagram", "tiktok", "threads"].includes(normalized)) {
    return "social-post";
  }
  return null;
}

function buildSurfaceOption(surface: string): CampaignSurfaceOption {
  const legacy = lookupLegacySurfaceOption(surface);
  if (legacy) return legacy;
  return {
    value: surface as Surface,
    label: humanizeSurface(surface),
    platform: inferPlatform(surface),
    format: inferFormat(surface),
  };
}

export function surfaceLabel(surface: Surface | string | null | undefined): string {
  if (!surface) return "Unspecified";
  return buildSurfaceOption(surface).label;
}

export function platformForSurface(surface: Surface | null | undefined): string | null {
  return surface ? buildSurfaceOption(surface).platform : null;
}

export function formatForSurface(surface: Surface | null | undefined): string | null {
  return surface ? buildSurfaceOption(surface).format : null;
}

export function datetimeLocalToIso(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/,
  );
  if (!match) return null;
  const [, year, month, day, hour, minute, second, milliseconds] = match;
  const paddedMilliseconds = milliseconds?.padEnd(3, "0");
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second ?? 0),
    Number(paddedMilliseconds ?? 0),
  );
  if (Number.isNaN(parsed.getTime())) return null;
  // The Date constructor normalizes missing spring-forward local times (for
  // example 02:30 to 03:30). Keep the user's entered wall time exact instead.
  if (
    parsed.getFullYear() !== Number(year) || parsed.getMonth() !== Number(month) - 1 || parsed.getDate() !== Number(day) ||
    parsed.getHours() !== Number(hour) || parsed.getMinutes() !== Number(minute) ||
    parsed.getSeconds() !== Number(second ?? 0) || parsed.getMilliseconds() !== Number(paddedMilliseconds ?? 0)
  ) return null;
  return parsed.toISOString();
}

export function datetimeLocalToIsoPreservingUntouched(
  value: string,
  initialValue: string,
  originalIso: string | null | undefined,
): string | null {
  return value === initialValue ? originalIso ?? null : datetimeLocalToIso(value);
}

export function isoToDatetimeLocal(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offsetMs = parsed.getTimezoneOffset() * 60_000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
}

export type CampaignSurfaceGuidance = {
  matchedSurface: BrandSurface | null;
  status: "matched" | "warning" | "missing";
  title: string;
  detail: string;
  warmUpPhase: WarmUpPhase | null;
};

function warmUpPhaseForSurface(surface: BrandSurface | null): WarmUpPhase | null {
  if (!surface || surface.category !== "participatory_identity") return null;
  const phase = (surface.config as { warm_up_phase?: unknown } | null | undefined)?.warm_up_phase;
  return phase === "cold" || phase === "warming" || phase === "established" ? phase : null;
}

function configuredCampaignSurfaceMappings(surface: BrandSurface): string[] {
  const configMappings = (surface.config as { campaign_surfaces?: unknown } | null | undefined)?.campaign_surfaces;
  if (Array.isArray(configMappings)) {
    const mapped = configMappings.filter((value): value is Surface => typeof value === "string");
    if (mapped.length > 0) return mapped;
  }
  return [];
}

function isCampaignEligibleSurface(surface: BrandSurface): boolean {
  if (surface.status === "retired") return false;
  if (surface.kind.trim().toLowerCase() === "bluesky") return false;
  if (surface.category === "algorithmic_discovery") return false;
  if (DESTINATION_SURFACE_KINDS.has(surface.kind.trim().toLowerCase())) return false;
  return surface.category === "owned_publication" || surface.category === "participatory_identity";
}

export function buildCampaignSurfaceOptions(
  brandSurfaces: BrandSurface[] | null | undefined,
): CampaignSurfaceOption[] {
  const values = new Map<string, CampaignSurfaceOption>();

  for (const surface of brandSurfaces ?? []) {
    if (!isCampaignEligibleSurface(surface)) continue;
    const configuredMappings = configuredCampaignSurfaceMappings(surface);
    if (configuredMappings.length > 0) {
      for (const mapping of configuredMappings) {
        values.set(mapping, buildSurfaceOption(mapping));
      }
      continue;
    }
    values.set(surface.kind, buildSurfaceOption(surface.kind));
  }

  const options = [...values.values()].sort((a, b) => a.label.localeCompare(b.label));
  return options.length > 0
    ? options
    : DEFAULT_CAMPAIGN_SURFACE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        platform: option.platform,
        format: option.format,
      }));
}

export function pickDefaultCampaignSurface(
  brandSurfaces: BrandSurface[] | null | undefined,
  preferredSurface?: Surface | null,
): Surface {
  const options = buildCampaignSurfaceOptions(brandSurfaces);
  if (preferredSurface && options.some((option) => option.value === preferredSurface)) {
    return preferredSurface;
  }
  return options[0]?.value ?? "linkedin";
}

function campaignSurfaceMatchTier(surface: BrandSurface, campaignSurface: Surface): number | null {
  if (surface.kind.trim().toLowerCase() === "bluesky" || String(campaignSurface) === "bluesky") return null;
  if (campaignSurface === "youtube") {
    return surface.kind === "youtube" && surface.category === "owned_publication" ? 1 : null;
  }
  if (campaignSurface === "youtube_personal") {
    if (surface.kind !== "youtube" || surface.category !== "owned_publication") return null;
    const configuredMappings = configuredCampaignSurfaceMappings(surface);
    return configuredMappings.includes(campaignSurface) ? 3 : null;
  }
  if (campaignSurface === "linkedin") {
    if (surface.kind === "linkedin" && surface.category === "participatory_identity") return 3;
    if (surface.kind === "linkedin_community" && surface.category === "participatory_identity") return 1;
    return null;
  }
  if (campaignSurface === "linkedin_personal" || campaignSurface === "linkedin_derivative") {
    if (surface.kind !== "linkedin_community" || surface.category !== "participatory_identity") {
      return null;
    }
    const configuredMappings = configuredCampaignSurfaceMappings(surface);
    return configuredMappings.includes(campaignSurface) ? 3 : null;
  }
  if (surface.category === "owned_publication") {
    const configuredMappings = configuredCampaignSurfaceMappings(surface);
    if (configuredMappings.length > 0) {
      return configuredMappings.includes(campaignSurface) ? 3 : null;
    }
    return surface.kind === campaignSurface ? 1 : null;
  }
  if (surface.kind !== "linkedin_community" || surface.category !== "participatory_identity") {
    return null;
  }

  const configuredMappings = configuredCampaignSurfaceMappings(surface);
  if (configuredMappings.length > 0) {
    return configuredMappings.includes(campaignSurface) ? 3 : null;
  }

  return 1;
}

export function findBrandSurfaceForCampaignSurface(
  brandSurfaces: BrandSurface[] | null | undefined,
  campaignSurface: Surface,
): BrandSurface | null {
  if (!brandSurfaces?.length) return null;

  const candidates = brandSurfaces
    .map((surface) => ({ surface, tier: campaignSurfaceMatchTier(surface, campaignSurface) }))
    .filter((candidate): candidate is { surface: BrandSurface; tier: number } => candidate.tier != null);

  if (candidates.length === 0) return null;
  return [...candidates]
    .sort((a, b) => b.tier - a.tier || b.surface.priority - a.surface.priority)[0]?.surface ?? null;
}

export function getCampaignSurfaceGuidance(
  brandSurfaces: BrandSurface[] | null | undefined,
  campaignSurface: Surface,
): CampaignSurfaceGuidance {
  const matchedSurface = findBrandSurfaceForCampaignSurface(brandSurfaces, campaignSurface);
  if (!matchedSurface) {
    return {
      matchedSurface: null,
      status: "missing",
      title: "No matching brand surface configured",
      detail: "This campaign surface is still using the legacy selector only. Add or map a matching surface in Settings → Surfaces for warm-up and health context.",
      warmUpPhase: null,
    };
  }

  const warmUpPhase = warmUpPhaseForSurface(matchedSurface);
  if (warmUpPhase && warmUpPhase !== "established") {
    return {
      matchedSurface,
      status: "warning",
      title: `${matchedSurface.display_name} is still warming up`,
      detail: `Warm-up phase is ${warmUpPhase}. Manual execution is still possible, but this surface should be handled cautiously until it is established.`,
      warmUpPhase,
    };
  }

  return {
    matchedSurface,
    status: "matched",
    title: `Matched to ${matchedSurface.display_name}`,
    detail:
      matchedSurface.category === "participatory_identity"
        ? "This target surface is configured and established in Surfaces."
        : "This target surface is configured in Surfaces.",
    warmUpPhase,
  };
}
